import { gql } from '@apollo/client';

// ==================== Fragments ====================

export const PRODUCT_FRAGMENT = gql`
  fragment ProductFields on Product {
    id
    sku
    name
    description
    unitPrice
    category
    isActive
    createdAt
    updatedAt
  }
`;

export const PRICE_HISTORY_FRAGMENT = gql`
  fragment PriceHistoryFields on PriceHistoryEntry {
    id
    price
    effectiveDate
    updatedBy
    createdAt
  }
`;

export const PRODUCT_WITH_HISTORY_FRAGMENT = gql`
  fragment ProductWithHistoryFields on Product {
    ...ProductFields
    priceHistory {
      ...PriceHistoryFields
    }
  }
  ${PRODUCT_FRAGMENT}
  ${PRICE_HISTORY_FRAGMENT}
`;

// ==================== Queries ====================

export const GET_PRODUCTS = gql`
  query GetProducts(
    $search: String
    $category: ProductCategory
    $isActive: Boolean
    $limit: Int
    $offset: Int
  ) {
    products(
      search: $search
      category: $category
      isActive: $isActive
      limit: $limit
      offset: $offset
    ) {
      items {
        ...ProductFields
        priceHistory {
          ...PriceHistoryFields
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        currentPage
      }
    }
  }
  ${PRODUCT_FRAGMENT}
  ${PRICE_HISTORY_FRAGMENT}
`;

export const GET_PRODUCT = gql`
  query GetProduct($id: ID!) {
    product(id: $id) {
      ...ProductWithHistoryFields
    }
  }
  ${PRODUCT_WITH_HISTORY_FRAGMENT}
`;

// ==================== Mutations ====================

export const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      ...ProductFields
    }
  }
  ${PRODUCT_FRAGMENT}
`;

export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($id: ID!, $input: UpdateProductInput!) {
    updateProduct(id: $id, input: $input) {
      ...ProductWithHistoryFields
    }
  }
  ${PRODUCT_WITH_HISTORY_FRAGMENT}
`;

export const DELETE_PRODUCT = gql`
  mutation DeleteProduct($id: ID!) {
    deleteProduct(id: $id)
  }
`;

export const ARCHIVE_PRODUCT = gql`
  mutation ArchiveProduct($id: ID!) {
    archiveProduct(id: $id)
  }
`;

export const UNARCHIVE_PRODUCT = gql`
  mutation UnarchiveProduct($id: ID!) {
    unarchiveProduct(id: $id)
  }
`;

// ==================== TypeScript Types ====================

export type ProductCategory = 'SOFTWARE' | 'SERVICES';

export interface PriceHistoryEntry {
  id: string;
  price: number;
  effectiveDate: string;
  updatedBy: string;
  createdAt: string;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string;
  unitPrice: number;
  category: ProductCategory;
  isActive: boolean;
  priceHistory: PriceHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export interface ProductConnection {
  items: Product[];
  pageInfo: PageInfo;
}

// Query variable types
export interface GetProductsVariables {
  search?: string;
  category?: ProductCategory;
  isActive?: boolean;
  limit?: number;
  offset?: number;
}

export interface GetProductVariables {
  id: string;
}

// Mutation input types
export interface CreateProductInput {
  sku: string;
  name: string;
  description?: string;
  unitPrice: number;
  category: ProductCategory;
  isActive?: boolean;
}

export interface UpdateProductInput {
  sku?: string;
  name?: string;
  description?: string;
  unitPrice?: number;
  category?: ProductCategory;
  isActive?: boolean;
}

// Query/Mutation response types
export interface GetProductsData {
  products: ProductConnection;
}

export interface GetProductData {
  product: Product | null;
}

export interface CreateProductData {
  createProduct: Product;
}

export interface UpdateProductData {
  updateProduct: Product;
}

export interface DeleteProductData {
  deleteProduct: boolean;
}

export interface ArchiveProductData {
  archiveProduct: boolean;
}

export interface UnarchiveProductData {
  unarchiveProduct: boolean;
}

// ==================== Helper Functions ====================

/**
 * Map category to display label
 */
export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  SOFTWARE: 'Software',
  SERVICES: 'Services',
};
